import { BaseResponse, IBaseResponse } from '../models/comm-http/responses/BaseResponse';
// import { AppModule } from '../app.module';

import { OnDestroy } from '@angular/core';
import { SweetAlertService } from 'ng2-cli-sweetalert2';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import { DialogService } from '../services/modal-dialogs';
import { ssApiService } from '../services/ss-api.service';
import { MemberEventsService } from '../services/member.events.service';
import { GlobalService } from '../services/global.service';
import { AppModule } from '../../app/app.module';

export class EntityChangedArgs<T> {
    Result: boolean;
    ResultMessage: string;
    Original: T;
    New: T;
}

export class AddSavedArgs<T>{
    Result: boolean;
    ResultMessage: string;
    Entity: T;
}

export enum ManagerModeTypes {
    Add = 0,
    Edit = 1
}


export class ManagerBase<T> implements OnDestroy {

    protected onDestroy;
    protected api: ssApiService;
    protected dialogService: DialogService;
    protected sweetAlertService: SweetAlertService;
    protected memberEvents: MemberEventsService;
    protected globalService: GlobalService;

    protected isBusy: boolean = false;
    protected original: T;

    public OnIsBusyChanged = new Subject<boolean>();
    public OnChangesSaveResponse = new Subject<EntityChangedArgs<T>>();
    public onSuccessAdd = new Subject<AddSavedArgs<T>>();
    public OnSuccessEdit = new Subject<T>();
    public OnErrorEdit = new Subject<string>();
    public OnDataLoaded = new Subject();

    public OnMemberNavigate = new Subject<number>();

    public newEntity: T;

    protected LoadAction: Observable<IBaseResponse<T>>;
    protected SaveAction: Observable<IBaseResponse<String>>;
    protected ShowAfterLoaded: boolean;
    protected DialogAction: Observable<boolean>;


    protected ManagerModeType: ManagerModeTypes = ManagerModeTypes.Edit;

    constructor(  ) {
        this.initServices();
    }

    ngOnDestroy() {
        this.onDestroy();
    }

    protected initServices() {
        this.api = AppModule.injector.get(ssApiService);
        this.dialogService = AppModule.injector.get(DialogService);
        this.sweetAlertService = AppModule.injector.get(SweetAlertService);
        this.memberEvents = AppModule.injector.get(MemberEventsService);
        this.globalService = AppModule.injector.get(GlobalService);
    }

    IsBusy(): boolean {
        return this.isBusy;
    }

    Original(): T {
        return this.original;
    }

    protected SetIsBusy(busy: boolean) {
        this.isBusy = busy;
        this.OnIsBusyChanged.next(busy);
    }

    protected SetModetype(type: ManagerModeTypes) {
        this.ManagerModeType = type;
    }

    protected CloneToOriginal(): void {

        this.original = JSON.parse(JSON.stringify(this.newEntity));

    }

    public UndoChanges(): void {
        this.newEntity = JSON.parse(JSON.stringify(this.original));
    }

    protected GetEntityChangedArgs(result: boolean, resultMessage: string): EntityChangedArgs<T> {
        let args = new EntityChangedArgs<T>();
        args.New = this.newEntity;
        args.Original = this.original;
        args.Result = result;
        args.ResultMessage = resultMessage;

        return args;
    }

    protected EmitEntityChanged(result: boolean, resultMessage: string) {
        let args = this.GetEntityChangedArgs(result, resultMessage);
        this.OnChangesSaveResponse.next(args);

        // other events based on type
        if ((<any>this).constructor.name == 'NoteManager')
            this.memberEvents.onNoteChanged.next(<any>args);


        if ((<any>this).constructor.name == 'MemberManager')
            this.memberEvents.onMemberChanged.next(<any>args);
    }

    public ModeType(): ManagerModeTypes {
        return this.ManagerModeType;
    }

    ShowBase() {

        this.DialogAction.subscribe((isConfirmed) => {
            // We get dialog result
            if (isConfirmed) {
                // ok
                return;
            } else {
                // alert('declined');
            }
        });

    }

    protected SetNewEntity(entity: T) {
        this.newEntity = entity;
        this.CloneToOriginal();
        this.OnDataLoaded.next();
    }

    protected LoadBase() {

        this.SetIsBusy(true);

        this.LoadAction.subscribe(
            (result: BaseResponse<T>) => {

                if (!result.Result) {
                    // error or no result
                    this.SetIsBusy(false);

                    this.sweetAlertService.swal({
                        title: 'Info',
                        text: 'The item you are trying to load does not exist.',
                        type: 'warning',
                        showCancelButton: true,
                        showConfirmButton: false,
                        timer: 2500
                    })
                        .then(() => { }, () => { }); // keep this line to prevent promise error;

                    return;
                }

                this.SetNewEntity(result.Data);

                if (this.ShowAfterLoaded)
                    this.ShowBase();

                this.SetIsBusy(false);
            },

            (error: any) => {
                this.SetIsBusy(false);
            }


        );

    }

    public SaveChangesBase(confirm: boolean = true, showResult: boolean = true) {


        if (!confirm) {
            this.LiveSaveChangesBase(showResult);
            return;
        }

        // confirm
        this.sweetAlertService.swal({
            title: 'Are you sure?',
            text: 'You will not be able to recover the changes!',
            type: 'warning',
            showCancelButton: true,
            showCloseButton: true,
            confirmButtonText: 'Yes, save!',
            cancelButtonText: 'No, cancel',
            allowOutsideClick: false,
            showLoaderOnConfirm: true,
            preConfirm: () => {
                return new Promise((resolve, reject) => {
                    setTimeout(() => {
                        this.LiveSaveChangesBase(showResult);
                        resolve();
                    }, 2500);
                });
            }
        }).then(
            (result) => {
                // this.sweetAlertService.swal({
                //     type: 'success',
                //     title: 'Successfully Saved',
                //     text: 'Saved without any errors.',
                //     timer: 2500
                // }).then( () => {} );
            },

            (dismiss) => {
                if (dismiss === 'cancel') {

                    if (!confirm)
                        return;

                    // show cancelled
                    this.sweetAlertService.swal({
                        title: 'Cancelled',
                        text: 'No changes are saved.',
                        type: 'error',
                        showCancelButton: false,
                        showConfirmButton: false,
                        timer: 1500
                    })
                        .then(() => { }, () => { }); // keep this line to prevent promise error;
                }
            });

    }

    private LiveSaveChangesBase(showResult: boolean = true) {

        this.SetIsBusy(true);
        this.SaveAction.subscribe(

            (result: BaseResponse<string>) => {

                this.SetIsBusy(false);
                if (result == null)
                    return;


                this.EmitEntityChanged(result.Result, result.Data);

                if (result.Result && this.ModeType() == ManagerModeTypes.Add) {

                    let args = new AddSavedArgs<T>();
                    args.Entity = this.newEntity;
                    args.Result = result.Result;
                    args.ResultMessage = result.Message;

                    this.onSuccessAdd.next(args);

                } else if (result.Result && this.ModeType() == ManagerModeTypes.Edit) {
                    this.OnSuccessEdit.next(this.newEntity);
                } else if (!result.Result) {
                    this.OnErrorEdit.next(result.Data);
                }


                if (!showResult)
                    return;

                // result
                if (result.Result) {
                    // ok
                    this.SetNewEntity(this.newEntity);
                    // this.sweetAlertService.swal(
                    //     'Saved!',
                    //     'Your changes have been saved.',
                    //     'success'
                    // )
                    //     .then(() => { }, () => { }); // keep this line to prevent promise error;
                } else {
                    // error
                    this.sweetAlertService.swal(
                        'Error',
                        result.Data,
                        'error'
                    )
                        .then(() => { }, () => { }); // keep this line to prevent promise error;
                }
            },

            (error: any) => {
                this.SetIsBusy(false);

                if (!showResult)
                    return;

                // error
                this.sweetAlertService.swal(
                    'Error',
                    'Unknown Error :-(',
                    'error'
                )
                    .then(() => { }, () => { }); // keep this line to prevent promise error;
            }
        );
    }

    NavigateToMember( memberID: number ) {
        this.OnMemberNavigate.next(memberID);
    }

}
